import axios from 'axios';
import { isConnected } from '../composables/useConnectivityStatus';

/**
 * Centralized API service with built-in caching
 * Reduces duplicate API calls and improves performance
 */
class ApiService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    this.pendingRequests = new Map(); // Prevent duplicate concurrent requests
    // Use environment variable or fall back to current origin
    this.baseURL = import.meta.env.VITE_API_BASE_URL || window.location.origin;
  }

  /**
   * Get cache key for a request
   */
  getCacheKey(url, params = {}) {
    const paramString = Object.keys(params).length > 0 ? JSON.stringify(params) : '';
    return `${url}${paramString}`;
  }

  /**
   * Check if cached data is still valid
   */
  isCacheValid(cacheEntry) {
    return Date.now() - cacheEntry.timestamp < this.cacheTimeout;
  }

  /**
   * Check if we're offline
   */
  isOffline() {
    return !isConnected.value;
  }

  /**
   * Try to load API response from browser cache storage
   */
  async getCachedApiResponse(fullUrl, originalUrl) {
    if (typeof window === 'undefined' || !('caches' in window)) {
      return null;
    }

    const candidates = [fullUrl, originalUrl].filter(Boolean);
    const preferredCaches = ['app-cache-v1-api', 'isotopes-v3-api'];

    try {
      const allCacheNames = await caches.keys();
      const orderedCacheNames = [
        ...preferredCaches.filter((name) => allCacheNames.includes(name)),
        ...allCacheNames.filter((name) => !preferredCaches.includes(name)),
      ];

      for (const cacheName of orderedCacheNames) {
        const cache = await caches.open(cacheName);

        for (const candidate of candidates) {
          const cachedResponse = await cache.match(candidate);
          if (!cachedResponse || !cachedResponse.ok) {
            continue;
          }

          try {
            const cachedJson = await cachedResponse.clone().json();

            console.info('[Offline] API cache fallback used:', candidate, `(${cacheName})`);

            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('app:api-cached-fallback', {
                detail: { url: candidate, cacheName },
              }));
            }

            return cachedJson;
          } catch (_) {
            // Ignore non-JSON cached entries and continue searching
          }
        }
      }
    } catch (error) {
      console.warn('Cache lookup failed for API response:', fullUrl, error);
    }

    return null;
  }

  /**
   * Make a simple request with basic caching
   */
  async cachedRequest(url) {
    // Build full URL using base URL
    const fullUrl = url.startsWith('http') ? url : `${this.baseURL}${url}`;

    if (this.isOffline()) {
      const cachedFallback = await this.getCachedApiResponse(fullUrl, url);
      if (cachedFallback) {
        return cachedFallback;
      }

      throw new Error(`Offline and no cached API response available for ${fullUrl}`);
    }
    
    // Simple fetch without complex caching logic
    try {
      const response = await fetch(fullUrl, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const cachedFallback = await this.getCachedApiResponse(fullUrl, url);
        if (cachedFallback) {
          return cachedFallback;
        }

        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      const cachedFallback = await this.getCachedApiResponse(fullUrl, url);
      if (cachedFallback) {
        return cachedFallback;
      }

      console.error('API request failed:', fullUrl, error);
      throw error;
    }
  }

  /**
   * Get all pages with caching
   */
  async getPages() {
    return this.cachedRequest('/api/pages');
  }

  /**
   * Get settings with caching
   */
  async getSettings() {
    return this.cachedRequest('/api/settings?populate=*');
  }

  /**
   * Get isotopes with caching
   */
  async getIsotopes() {
    return this.cachedRequest('/api/isotopes');
  }

  /**
   * Get page components with caching
   */
  async getPageComponents(slug, componentType) {
    return this.cachedRequest(`/api/pages/${slug}/components/${componentType}`);
  }

  /**
   * Get filtered pages (menu pages only)
   */
  async getMenuPages() {
    const data = await this.getPages();
    const pages = Array.isArray(data.pages) ? data.pages : Array.isArray(data) ? data : [];
    return pages.filter(page => page.addToMenu === true);
  }

  /**
   * Clear cache (useful for testing or forced refresh)
   */
  clearCache() {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  /**
   * Clear specific cache entry
   */
  clearCacheEntry(url, params = {}) {
    const cacheKey = this.getCacheKey(url, params);
    this.cache.delete(cacheKey);
  }

  /**
   * POST request (not cached)
   */
  async post(url, data, options = {}) {
    // Build full URL using base URL, same as GET requests
    const fullUrl = url.startsWith('http') ? url : `${this.baseURL}${url}`;

    if (this.isOffline()) {
      const error = new Error(`Offline and unable to POST to ${fullUrl}`);
      error.code = 'ERR_NETWORK';
      throw error;
    }

    return axios.post(fullUrl, data, options);
  }
}

// Export singleton instance
export default new ApiService();
